import { useAuth0 } from '@auth0/auth0-react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';

/**
 * Identifies the LaunchDarkly evaluation context with the signed-in user.
 *
 * The app boots LaunchDarkly with an anonymous context, which carries no attributes. Any flag rule
 * that targets a person — "serve true to jyu@integritysystems.com.au" — matches on the context's
 * `email` *attribute*, not on its key, so an anonymous context can never satisfy one. This swaps in
 * an identified context once Auth0 has resolved, after which targeted rules evaluate correctly.
 *
 * Mirrors GTMUserTracker: renders nothing, lives inside Auth0ProviderWithHistory, and does its work
 * in an effect.
 */
const LaunchDarklyUserTracker: React.FC = () => {
    const { isAuthenticated, user, isLoading } = useAuth0();
    const ldClient = useLDClient();

    useEffect(() => {
        if (isLoading || !isAuthenticated || !user?.email || !ldClient) return;

        ldClient
            .identify({
                kind: 'user',
                // The Auth0 subject is the stable per-user key; email is what targeting rules read.
                key: user.sub ?? user.email,
                email: user.email,
            })
            // A failed identify must never break the app — flags simply stay at their defaults.
            .catch(() => undefined);
    }, [isAuthenticated, user, isLoading, ldClient]);

    return null;
};

export default LaunchDarklyUserTracker;
